import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, UserCheck, UserX, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Layout from '../components/Layout';
import { usuariosService } from '../services/usuariosService';
import type { Usuario } from '../services/usuariosService';
import UsuarioFormModal from '../components/UsuarioFormModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import {
  Container,
  PageHeader as StyledPageHeader,
  PageTitle as StyledPageTitle,
  Text,
  BaseButton,
  Card,
  SearchInput as StyledSearchInput,
  Select,
  Flex,
  LoadingSpinner as StyledLoadingSpinner,
  EmptyState as StyledEmptyState,
  EmptyStateIcon,
  EmptyStateText,
  theme
} from '../styles';
import {
  HeaderLeft,
  HeaderActions,
  SearchInputWrapper,
  SearchIcon,
  UsersGrid,
  UserHeader,
  UserAvatar,
  UserInfo,
  UserName,
  UserEmail,
  UserMeta,
  UserRole,
  UserStatus,
  UserActions,
  ActionButton
} from './UsuariosPage.styles';

const UsuariosPage: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [tipoFilter, setTipoFilter] = useState('todos');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState<Usuario | null>(null);
  const [editingUsuario, setEditingUsuario] = useState<Usuario | null>(null);

  useEffect(() => {
    carregarUsuarios();
  }, []);

  useEffect(() => {
    aplicarFiltros();
  }, [usuarios, searchTerm, statusFilter, tipoFilter]);

  const carregarUsuarios = async () => {
    try {
      setLoading(true);
      const data = await usuariosService.listarUsuarios();
      setUsuarios(data);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let filtered = [...usuarios];

    // Filtro por termo de busca
    if (searchTerm) {
      filtered = filtered.filter(usuario =>
        usuario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por status
    if (statusFilter !== 'todos') {
      filtered = filtered.filter(usuario =>
        statusFilter === 'ativo' ? usuario.ativo : !usuario.ativo
      );
    }

    // Filtro por tipo
    if (tipoFilter !== 'todos') {
      filtered = filtered.filter(usuario => usuario.tipo === tipoFilter);
    }

    setFilteredUsuarios(filtered);
  };

  const handleCreateUsuario = () => {
    setEditingUsuario(null);
    setShowFormModal(true);
  };

  const handleEditUsuario = (usuario: Usuario) => {
    setEditingUsuario(usuario);
    setShowFormModal(true);
  };

  const handleDeleteUsuario = (usuario: Usuario) => {
    setSelectedUsuario(usuario);
    setShowDeleteModal(true);
  };

  const handleToggleStatus = async (usuario: Usuario) => {
    try {
      const updatedUsuario = await usuariosService.toggleUsuarioAtivo(usuario.id);
      setUsuarios(prev => prev.map(u => u.id === usuario.id ? updatedUsuario : u));
      toast.success(
        `Usuário ${updatedUsuario.ativo ? 'ativado' : 'desativado'} com sucesso!`
      );
    } catch (error: any) {
      toast.error(error.message || 'Erro ao alterar status do usuário');
    }
  };

  const handleFormSubmit = async (success: boolean) => {
    if (success) {
      await carregarUsuarios();
    }
    setShowFormModal(false);
    setEditingUsuario(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUsuario) return;

    try {
      await usuariosService.excluirUsuario(selectedUsuario.id);
      setUsuarios(prev => prev.filter(u => u.id !== selectedUsuario.id));
      toast.success('Usuário excluído com sucesso!');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao excluir usuário');
    }

    setShowDeleteModal(false);
    setSelectedUsuario(null);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleLabel = (tipo: 'admin' | 'operador') => {
    return tipo === 'admin' ? 'Administrador' : 'Operador';
  };

  const getStatusLabel = (ativo: boolean) => {
    return ativo ? 'Ativo' : 'Inativo';
  };

  return (
    <Layout title="Gestão de Usuários">
      <Container>
        <StyledPageHeader>
          <HeaderLeft>
            <StyledPageTitle>
              <Users />
              Usuários
            </StyledPageTitle>
            <Text size="lg" color="secondary">
              Gerencie os usuários que têm acesso ao sistema
            </Text>
          </HeaderLeft>
          <HeaderActions>
            <BaseButton variant="primary" onClick={handleCreateUsuario}>
              <Plus />
              Novo Usuário
            </BaseButton>
          </HeaderActions>
        </StyledPageHeader>

        <Card style={{ marginBottom: theme.spacing[6] }}>
          <Flex gap={theme.spacing[4]} align="center" wrap>
            <SearchInputWrapper>
              <SearchIcon>
                <Search size={20} />
              </SearchIcon>
              <StyledSearchInput
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '44px' }}
              />
            </SearchInputWrapper>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="todos">Todos os status</option>
              <option value="ativo">Apenas ativos</option>
              <option value="inativo">Apenas inativos</option>
            </Select>

            <Select
              value={tipoFilter}
              onChange={(e) => setTipoFilter(e.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="todos">Todos os tipos</option>
              <option value="admin">Administradores</option>
              <option value="operador">Operadores</option>
            </Select>
          </Flex>
        </Card>

        {loading ? (
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <StyledLoadingSpinner />
          </Flex>
        ) : filteredUsuarios.length === 0 ? (
          <StyledEmptyState>
            <EmptyStateIcon>
              <Users size={32} />
            </EmptyStateIcon>
            <EmptyStateText>
              {searchTerm || statusFilter !== 'todos' || tipoFilter !== 'todos'
                ? 'Nenhum usuário encontrado'
                : 'Nenhum usuário cadastrado'
              }
            </EmptyStateText>
            <Text size="sm" color="secondary" style={{ marginBottom: theme.spacing[4] }}>
              {searchTerm || statusFilter !== 'todos' || tipoFilter !== 'todos'
                ? 'Tente ajustar os filtros para encontrar outros usuários.'
                : 'Clique em "Novo Usuário" para adicionar o primeiro usuário.'
              }
            </Text>
          </StyledEmptyState>
        ) : (
          <UsersGrid>
            {filteredUsuarios.map((usuario) => (
              <Card key={usuario.id}>
                <UserHeader>
                  <UserAvatar type={usuario.tipo}>
                    {getInitials(usuario.nome)}
                  </UserAvatar>
                  <UserInfo>
                    <UserName>{usuario.nome}</UserName>
                    <UserEmail>{usuario.email}</UserEmail>
                  </UserInfo>
                </UserHeader>

                <UserMeta>
                  <UserRole type={usuario.tipo}>
                    {getRoleLabel(usuario.tipo)}
                  </UserRole>
                  <UserStatus ativo={usuario.ativo}>
                    {usuario.ativo ? <UserCheck size={12} /> : <UserX size={12} />}
                    {getStatusLabel(usuario.ativo)}
                  </UserStatus>
                </UserMeta>

                <UserActions>
                  <ActionButton onClick={() => handleEditUsuario(usuario)}>
                    <Edit />
                  </ActionButton>
                  <ActionButton
                    variant={usuario.ativo ? 'warning' : 'success'}
                    onClick={() => handleToggleStatus(usuario)}
                  >
                    {usuario.ativo ? <UserX /> : <UserCheck />}
                  </ActionButton>
                  <ActionButton
                    variant="danger"
                    onClick={() => handleDeleteUsuario(usuario)}
                  >
                    <Trash2 />
                  </ActionButton>
                </UserActions>
              </Card>
            ))}
          </UsersGrid>
        )}
      </Container>

      {showFormModal && (
        <UsuarioFormModal
          usuario={editingUsuario}
          onClose={() => setShowFormModal(false)}
          onSubmit={handleFormSubmit}
        />
      )}

      {showDeleteModal && selectedUsuario && (
        <ConfirmDeleteModal
          title="Excluir Usuário"
          message={`Tem certeza que deseja excluir o usuário "${selectedUsuario.nome}"? Esta ação não pode ser desfeita.`}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </Layout>
  );
};

export default UsuariosPage;
